import {
    AfterViewInit,
    Component,
    computed,
    ElementRef,
    Inject,
    OnDestroy,
    OnInit,
    Renderer2,
    Signal,
    signal,
    ViewChild,
    WritableSignal
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    ValidatorFn,
    Validators
} from '@angular/forms';
import {
    BehaviorSubject,
    combineLatest,
    Observable,
    of,
    Subscription,
    throwError,
    TimeoutError
} from 'rxjs';
import { SettingsService } from '../../services/settings.service';
import {
    catchError,
    distinctUntilChanged,
    filter,
    map,
    startWith,
    switchMap
} from 'rxjs/operators';
import { CountrySetting } from '../../models/countrysetting';
import { PhoneNumberUtil, PhoneNumber } from 'google-libphonenumber';
import { SnackbarService } from '../../services/snackbar.sevice';
import { Constants } from '../../models/constants';
import { UserSettingsService } from '../../services/user-settings.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Title } from '../my-hospital-user.model';
import { ClientSite } from 'src/app/models/clientsite';
import { Site } from 'src/app/api-module/control-centre-api-client';
import {
    CreateUserRequestV2,
    Role,
    RoleEnum,
    UserOrg,
    UserRole
} from 'src/app/api-module/user-management-api-client-v2';
import { OrgSiteSelectionService } from 'src/app/shared/org-site-selection/org-site-selection.service';
import { RoleMappingService } from 'src/app/services/role-mapping.service';
import { siteBasedRoles } from '@app/referrals/referrals.model';
import { type SitePermissionsChanged } from '@app/shared/site-permission-editor-v2/site-permission-editor-v2.component';
import { ng18_createLinkedSignal } from '@app/shared/ng-polyfills';
import { stringIsNotNullUndefinedOrEmpty } from '@app/shared/utils/string-utils';

const phoneUtil = PhoneNumberUtil.getInstance();

export function requireCheckboxesToBeCheckedValidator(
    minRequired = 1
): ValidatorFn {
    return function validate(formGroup: UntypedFormGroup) {
        let checked = 0;
        Object.keys(formGroup.controls).forEach((key) => {
            const control = formGroup.controls[key];

            if (control.value === true) {
                checked++;
            }
        });

        if (checked < minRequired) {
            return {
                requireCheckboxesToBeChecked: true
            };
        }

        return null;
    };
}

function getPermittedSitesCompatibility(
    sites: Array<ClientSite>,
    permittedSiteIds: Array<number>
): Array<number> {
    const isOrgLevel =
        permittedSiteIds?.[0] === null && permittedSiteIds?.length === 1;

    if (isOrgLevel) {
        return sites.map((i) => i.siteId);
    }

    if (!permittedSiteIds || permittedSiteIds.length < 1) {
        return [];
    }

    const permittedClientSites = sites
        .filter((i) => permittedSiteIds.includes(i.siteId))
        .map((i) => i.siteId);

    return permittedClientSites;
}

function createPermittedSitesLinkedSignal(
    initialValue: number[],
    allSitesSignal: Signal<Array<ClientSite>>
): WritableSignal<number[]> {
    const permittedSiteIdsSignal = signal<number[]>(initialValue);
    const computedValue = computed(() => {
        const allSites = allSitesSignal();
        const permittedSiteIds = permittedSiteIdsSignal();
        const result = getPermittedSitesCompatibility(
            allSites,
            permittedSiteIds
        );
        return result;
    });

    const customSignal: WritableSignal<number[]> = (() =>
        computedValue()) as any;

    customSignal.set = (newValue: number[]) =>
        permittedSiteIdsSignal.set(newValue);
    customSignal.update = (updateFn: (newValue: number[]) => number[]) =>
        permittedSiteIdsSignal.update(updateFn);
    customSignal.asReadonly = () => computedValue;

    return customSignal;
}

@Component({
    selector: 'app-create-user',
    templateUrl: './create-user.component.html',
    styleUrls: ['./create-user.component.scss']
})
export class CreateUserComponent implements OnInit, AfterViewInit, OnDestroy {
    private readonly NAME_PATTERN =
        "[a-zA-ZÀ-ž\u0370-\u03FF\u0400-\u04FF][0-9a-zA-ZÀ-ž\u0370-\u03FF\u0400-\u04FF,.@'-_^ ]*";
    // Phone number related constants
    private readonly PHONE_PATTERN = '[0-9]*';
    private readonly MAX_SIZE_PHONE_EXTENSION = 10;
    private readonly subscriptions = new Subscription();

    RoleMappingService = RoleMappingService;

    @ViewChild('createUserContainer') createUserContainer: ElementRef;

    readonly roles: Role[];
    roleEnum = RoleEnum;
    newUserFormGroup: UntypedFormGroup;
    countryCode$: Observable<string>;

    hasSiteSpecificRoles = signal(false);

    countryCode: string;
    private readonly orgCode = new BehaviorSubject<string | undefined>(
        undefined
    );
    private readonly orgCode$ = this.orgCode.asObservable();
    readonly sitesForCurrentOrg = signal<ClientSite[]>([]);
    readonly permittedSiteIds = createPermittedSitesLinkedSignal(
        [],
        this.sitesForCurrentOrg
    );
    readonly permittedClientSites = computed(() => {
        const permittedSiteIds = this.permittedSiteIds();
        const result = this.sitesForCurrentOrg().filter((i) =>
            permittedSiteIds.includes(i.siteId)
        );
        return result;
    });

    selectedDefaultSiteId = ng18_createLinkedSignal<
        number[] | undefined,
        number | null
    >({
        source: () => this.permittedSiteIds(),
        computation: (newOptions, previous) => {
            const result = (() => {
                if (!newOptions || newOptions.length < 1) {
                    return null;
                }
                const valueInNewOptions = newOptions.find(
                    (opt) => opt === previous?.value
                );
                return valueInNewOptions ?? null;
            })();

            this.newUserFormGroup?.controls.defaultSite.setValue(result);
            this.newUserFormGroup?.controls.defaultSite.markAsDirty();

            return result;
        }
    });

    numSiteSelected = computed(() => {
        return this.permittedSiteIds().length;
    });

    saving = false;
    readonly titles: Title[] = [
        { value: 'Dr.', viewValue: 'Dr.' },
        { value: 'Miss', viewValue: 'Miss' },
        { value: 'Ms.', viewValue: 'Ms.' },
        { value: 'Mrs.', viewValue: 'Mrs.' },
        { value: 'Mr.', viewValue: 'Mr.' }
    ];

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: any,
        public dialogRef: MatDialogRef<CreateUserComponent>,
        private readonly orgSiteSelectionService: OrgSiteSelectionService,
        private readonly renderer: Renderer2,
        private readonly formBuilder: UntypedFormBuilder,
        private readonly settingsService: SettingsService,
        private readonly userSettingsService: UserSettingsService,
        private readonly snackbarService: SnackbarService
    ) {
        dialogRef.disableClose = true;
        this.roles = [...data.roles];
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    ngOnInit(): void {
        this.initOrg();
        this.initForm();
        this.initSitePermission();
    }

    ngAfterViewInit(): void {
        const createUserContainer = this.createUserContainer
            .nativeElement as HTMLElement;
        this.renderer.setStyle(
            createUserContainer,
            'width',
            createUserContainer.offsetWidth + 'px'
        );
    }

    get form() {
        return this.newUserFormGroup.controls;
    }

    getErrorMessage(
        response: HttpErrorResponse | TimeoutError
    ): Observable<never> {
        if (response instanceof TimeoutError) {
            console.error('Request timeout: ', JSON.stringify(response));
            return throwError(Constants.UnableToContactServerMessage);
        } else if (
            response instanceof HttpErrorResponse &&
            (response.error instanceof ProgressEvent ||
                response.status === 502 ||
                response.status === 503 ||
                response.status === 504)
        ) {
            console.error('Unable to contact server: ', response.status);
            return throwError(Constants.UnableToContactServerMessage);
        }
        console.error(
            `Server returned code ${
                response.status
            } with body:\n${JSON.stringify(response.error)}`
        );
        if (response.status === 403) {
            return throwError('You do not have permission to create this user');
        }
        if (response.status === 400) {
            // SPA shouldn't submit an invalid form. if it does and server returns error, it's an error scenario.
            const errorMessage =
                'Something went wrong while creating the user. Please try again in some time. ' +
                'If the problem persists, please contact Everlight support team';
            return throwError(errorMessage);
        } else if (response.status === 409) {
            if (response.error?.detail) {
                return throwError(response.error.detail);
            }
        }
        return throwError(
            'User creation failed. Please try again in some time. ' +
                'If the problem persists, please contact Everlight support team'
        );
    }

    submit() {
        this.markAsTouched(this.newUserFormGroup);
        const isRoleAndSiteValid = !(
            this.hasRoleSelected(RoleEnum.GeneralAccess) &&
            this.permittedSiteIds().length === 0
        );

        if (this.newUserFormGroup.valid && isRoleAndSiteValid) {
            this.saving = true;

            const userDetails = this.getUserDetailsFromForm();

            this.userSettingsService
                .createUserV2(userDetails)
                .pipe(catchError(this.getErrorMessage))
                .subscribe({
                    next: (_) => {
                        this.snackbarService.openSnackBar(
                            'The user has been successfully created.',
                            SnackbarService.SNACK_BAR_CLASS_SUCCESS
                        );
                        if (!this.form.isToCreateAnotherUser.value) {
                            this.dialogRef.close();
                        } else {
                            this.reset();
                            this.saving = false;
                        }
                    },
                    error: (error) => {
                        this.snackbarService.openSnackBar(
                            error,
                            SnackbarService.SNACK_BAR_CLASS_ALERT
                        );
                        this.saving = false;
                    }
                });
        } else {
            this.snackbarService.openSnackBar(
                'The user cannot be created. Please resolve the errors.',
                SnackbarService.SNACK_BAR_CLASS_ALERT
            );
            this.saving = false;
        }
    }

    cancelWindow(): void {
        this.dialogRef.close();
    }

    private initForm() {
        this.newUserFormGroup = this.formBuilder.group({
            title: [null],
            firstName: [
                null,
                [
                    Validators.required,
                    Validators.maxLength(64),
                    Validators.pattern(this.NAME_PATTERN)
                ]
            ],
            lastName: [
                null,
                [
                    Validators.required,
                    Validators.maxLength(64),
                    Validators.pattern(this.NAME_PATTERN)
                ]
            ],
            emailAddress: [
                null,
                [
                    Validators.required,
                    Validators.maxLength(128),
                    Validators.email
                ]
            ],
            contactPhone: [
                null,
                [
                    Validators.pattern(this.PHONE_PATTERN),
                    Validators.maxLength(15)
                ]
            ],
            contactPhoneExt: [
                null,
                [
                    Validators.pattern(this.PHONE_PATTERN),
                    Validators.maxLength(this.MAX_SIZE_PHONE_EXTENSION)
                ]
            ],
            rolesCheckboxGroup: new UntypedFormGroup(
                {},
                requireCheckboxesToBeCheckedValidator()
            ),
            defaultSite: [null, [Validators.nullValidator]],
            isToCreateAnotherUser: [null, Validators.nullValidator]
        });
        this.roles.forEach((role) => {
            if (!stringIsNotNullUndefinedOrEmpty(role.name)) {
                throw new Error(`Role with Id:${role.id} has no name defined`);
            }
            (this.form.rolesCheckboxGroup as UntypedFormGroup).addControl(
                RoleMappingService.getRoleEnum(role.name),
                new UntypedFormControl(false)
            );
        });

        const phoneChange$ = combineLatest([
            this.form.contactPhone.valueChanges as Observable<string>,
            this.countryCode$
        ]);

        this.subscriptions.add(
            phoneChange$.subscribe(([term, countryCode]) => {
                this.onPhoneNumberChanged(term, countryCode);
            })
        );

        const defaultSiteChange$ = this.form.defaultSite
            .valueChanges as Observable<number>;

        this.subscriptions.add(
            defaultSiteChange$.subscribe((newDefaultSiteId: number) => {
                this.selectedDefaultSiteId.set(newDefaultSiteId);
            })
        );
    }

    private initOrg() {
        this.subscriptions.add(
            this.orgSiteSelectionService
                .getOrgSelected$()
                .subscribe((selectedOrg) =>
                    this.orgCode.next(selectedOrg.orgCode)
                )
        );
        // Pipe to get client observable
        const client$ = this.orgSiteSelectionService.getOrgSelected$().pipe(
            switchMap((selectedOrg) => {
                if (!stringIsNotNullUndefinedOrEmpty(selectedOrg.orgCode)) {
                    throw new Error(
                        `Selected Org with Client Name:${selectedOrg.clientName} has no orgCode defined`
                    );
                }
                return this.settingsService.getClientByOrg(selectedOrg.orgCode);
            })
        );

        // Pipe to get CountrySetting[] observable
        const countrySettings$ = this.settingsService
            .getCountrySettings()
            .pipe(
                map((response) =>
                    response.map(
                        (item) =>
                            new CountrySetting(
                                item.country,
                                item.region,
                                item.countryCode
                            )
                    )
                )
            );

        // Pipe to return combined result of countryCode.
        this.countryCode$ = combineLatest([client$, countrySettings$]).pipe(
            map(
                ([client, countrySettings]) =>
                    countrySettings.filter(
                        (c) => c.country === client.countryCode
                    )[0].countryCode
            )
        );

        this.subscriptions.add(
            this.countryCode$.subscribe(
                (countryCode) => (this.countryCode = countryCode)
            )
        );
    }

    private initSitePermission() {
        this.subscriptions.add(
            this.orgCode$
                .pipe(
                    filter((org) => !!org),
                    distinctUntilChanged(),
                    switchMap((org) => this.settingsService.getSitesByOrg(org)),
                    map((sites: Site[]) =>
                        sites.map(
                            (item) => new ClientSite(item.id, item.siteName)
                        )
                    ),
                    switchMap((sites: ClientSite[]) => {
                        const sorted = [...sites].sort(
                            (siteA: ClientSite, siteB: ClientSite) =>
                                siteA.siteName.localeCompare(siteB.siteName)
                        );
                        return of(sorted);
                    }),
                    startWith([])
                )
                .subscribe((value) => {
                    this.sitesForCurrentOrg.set(value);
                })
        );

        const siteBasedRoleCheckboxes = siteBasedRoles
            .map((role) => this.form.rolesCheckboxGroup.get(role))
            .filter((checkBox) => !!checkBox);
        this.subscriptions.add(
            combineLatest(
                siteBasedRoleCheckboxes.map((checkBox) =>
                    checkBox.valueChanges.pipe(startWith(false))
                )
            ).subscribe((siteBasedRoleSelections: boolean[]) => {
                const hasSiteSpecificRole =
                    siteBasedRoleSelections.includes(true);
                this.hasSiteSpecificRoles.set(hasSiteSpecificRole);
                const sitesForCurrentOrg = this.sitesForCurrentOrg();
                if (sitesForCurrentOrg.length === 1) {
                    if (hasSiteSpecificRole) {
                        this.permittedSiteIds.set([
                            sitesForCurrentOrg[0].siteId
                        ]);
                        this.form.defaultSite.setValue(
                            sitesForCurrentOrg[0].siteId
                        );
                        this.form.defaultSite.markAsDirty();
                    } else {
                        this.permittedSiteIds.set([0]);
                        this.form.defaultSite.setValue(0);
                        this.form.defaultSite.markAsDirty();
                    }
                }
            })
        );
    }

    private onPhoneNumberChanged(contactNumber: string, countryCode: string) {
        if (!contactNumber) {
            this.form.contactPhoneExt.reset();
            return;
        }
        const phoneNumberNew = countryCode + ' ' + contactNumber;

        let validNumber = false;
        let phoneNumber: PhoneNumber;
        try {
            phoneNumber = phoneUtil.parseAndKeepRawInput(phoneNumberNew, '');
            validNumber = phoneUtil.isValidNumber(phoneNumber);
        } catch (e) {}

        if (validNumber) {
            this.form.contactPhone.setValue(
                phoneNumber.getNationalNumber()?.toString(),
                { emitEvent: false }
            );
        } else {
            this.form.contactPhone.setErrors(
                { invalidphone: true },
                { emitEvent: false }
            );
        }
    }

    private markAsTouched(form: UntypedFormGroup) {
        form.markAllAsTouched();
    }

    getUserDetailsFromForm(): CreateUserRequestV2 {
        const userDetails = new CreateUserRequestV2({
            title: this.form.title.value,
            firstName: this.form.firstName.value,
            surname: this.form.lastName.value,
            emailAddress: this.form.emailAddress.value,
            phoneNumberCountryCode: this.countryCode,
            phoneNumber: this.form.contactPhone.value,
            phoneNumberExtension: this.form.contactPhoneExt.value,
            userOrg: new UserOrg({
                orgCode: this.orgCode.value,
                defaultSiteId:
                    this.form.defaultSite.value === 0
                        ? null
                        : this.form.defaultSite.value,
                roles: []
            })
        });

        Object.keys(
            (this.form.rolesCheckboxGroup as UntypedFormGroup).controls
        ).forEach((key: RoleEnum) => {
            switch (key) {
                case RoleEnum.Admin:
                    if (this.hasRoleSelected(RoleEnum.Admin)) {
                        userDetails.userOrg!.roles!.push(
                            new UserRole({
                                roleType: RoleEnum.Admin,
                                siteId: null
                            })
                        );
                    }
                    break;

                case RoleEnum.GeneralAccess:
                case RoleEnum.ClinicalFeedback:
                case RoleEnum.ImagingRequestViewer:
                case RoleEnum.ImagingRequestEditor:
                    this.applySelectedSites(userDetails, key);
                    break;

                default:
                    if (this.hasRoleSelected(key)) {
                        userDetails.userOrg!.roles!.push(
                            new UserRole({
                                roleType: key,
                                siteId: null
                            })
                        );
                    }
                    break;
            }
        });

        return userDetails;
    }

    private hasRoleSelected(role: RoleEnum): boolean {
        return this.form.rolesCheckboxGroup.get(role)?.value ?? false;
    }

    // this is nasty as the form may not exists...
    private applySelectedSites(
        userDetails: CreateUserRequestV2,
        role: RoleEnum
    ) {
        const permittedSiteIds = this.permittedSiteIds();

        if (!this.hasRoleSelected(role) || permittedSiteIds.length === 0) {
            return;
        }

        const availableClientSites = this.sitesForCurrentOrg();

        const hasAccessToAllAvailableSites =
            permittedSiteIds.length === availableClientSites.length;
        if (hasAccessToAllAvailableSites) {
            userDetails.userOrg?.roles?.push(
                new UserRole({
                    roleType: role,
                    siteId: null
                })
            );
            return;
        }

        availableClientSites
            .filter((i) => permittedSiteIds.includes(i.siteId))
            .forEach((info) => {
                userDetails.userOrg?.roles?.push(
                    new UserRole({
                        roleType: role,
                        siteId: info.siteId
                    })
                );
            });
    }

    private reset() {
        const isToCreateAnotherUser = this.form.isToCreateAnotherUser.value;
        this.newUserFormGroup.reset();
        this.form.isToCreateAnotherUser.setValue(isToCreateAnotherUser);
        this.permittedSiteIds.set([]);
    }

    onSitePermissionChanged(args: SitePermissionsChanged): void {
        this.permittedSiteIds.set(args.permittedSiteIds);
    }
}
